import { createSlice } from '@reduxjs/toolkit'
import { PlanningRecord } from '@leadgen/models/client'
import authActions from '../auth/actions'
import actions from './actions'

export interface PlanningRecordsState {
  planningRecords: PlanningRecord[]
  planningRecordIds: Record<string, boolean>
  error?: string
  status: 'initial' | 'idle' | 'loading' | 'loading-more' | 'failed'
}

export const initialState: PlanningRecordsState = {
  planningRecords: [],
  planningRecordIds: {},
  status: 'initial',
}

export const planningRecordsSlice = createSlice({
  name: 'planningRecords',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(authActions.loggedOut.type, () => {
        return initialState
      })
      .addCase(actions.loadPlanningRecordsFresh.pending, state => {
        state.status = 'loading'
      })
      .addCase(actions.loadPlanningRecordsFresh.fulfilled, (state, action) => {
        state.status = 'idle'
        state.planningRecords = action.payload.data
        state.planningRecordIds = action.payload.data.reduce(
          (memo, next) => {
            memo[next.id] = true
            return memo
          },
          <Record<string, boolean>>{},
        )
        state.error = undefined
      })
      .addCase(actions.loadPlanningRecordsFresh.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action.error.message
      })
      .addCase(actions.loadPlanningRecordsNext.pending, state => {
        state.status = 'loading-more'
      })
      .addCase(actions.loadPlanningRecordsNext.fulfilled, (state, action) => {
        state.status = 'idle'
        state.planningRecords.push(...action.payload.data)
        state.planningRecordIds = action.payload.data.reduce((memo, next) => {
          memo[next.id] = true
          return memo
        }, state.planningRecordIds)
        state.error = undefined
      })
      .addCase(actions.loadPlanningRecordsNext.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action.error.message
      })
      .addCase(actions.addPlanningRecord, (state, action) => {
        if (state.planningRecordIds[action.payload.id]) {
          return
        }
        if (!state.planningRecords.length) {
          state.planningRecordIds[action.payload.id] = true
          state.planningRecords.push(action.payload)
          return
        }
        for (let i = 0, length = state.planningRecords.length; i < length; i++) {
          const planningRecord = state.planningRecords[i]
          if (
            new Date(action.payload.createdTimestamp) > new Date(planningRecord.createdTimestamp)
          ) {
            state.planningRecordIds[action.payload.id] = true
            state.planningRecords.splice(i, 0, action.payload)
            return
          }
        }
        state.planningRecordIds[action.payload.id] = true
        state.planningRecords.push(action.payload)
      })
      .addCase(actions.updatePlanningRecord, (state, action) => {
        for (let i = 0, length = state.planningRecords.length; i < length; i++) {
          const planningRecord = state.planningRecords[i]
          if (planningRecord.id === action.payload.id) {
            state.planningRecords[i] = action.payload
            break
          }
        }
      })
      .addCase(actions.approvePlanningRecord, (state, action) => {
        for (let i = 0, length = state.planningRecords.length; i < length; i++) {
          const planningRecord = state.planningRecords[i]
          if (planningRecord.id === action.payload.id) {
            if (state.planningRecordIds[action.payload.id]) {
              delete state.planningRecordIds[action.payload.id]
            }
            state.planningRecords.splice(i, 1)
            break
          }
        }
      })
      .addCase(actions.discardPlanningRecord, (state, action) => {
        for (let i = 0, length = state.planningRecords.length; i < length; i++) {
          const planningRecord = state.planningRecords[i]
          if (planningRecord.id === action.payload.id) {
            if (state.planningRecordIds[action.payload.id]) {
              delete state.planningRecordIds[action.payload.id]
            }
            state.planningRecords.splice(i, 1)
            break
          }
        }
      })
  },
})

export default planningRecordsSlice
